import { Component, OnInit } from '@angular/core';
import { FormGroup, FormBuilder, Validators } from '@angular/forms';
import { DeliveryLocationsJeevesComponent, DeliveryLocationsService } from 'gung-standard-jeeves';
import { SelectedCustomerService, AuthService } from 'gung-standard';
import { forkJoin, of } from 'rxjs';
import { first, mergeMap } from 'rxjs';
import { environment } from './../../../../../environments/environment';
import { TyloCustomerUtilsService } from '../../../../services/utils/tylo-customer-utils.service';

@Component({
  selector: 'tylo-helo-delivery-locations',
  templateUrl: './helo-delivery-locations.component.html',
  styleUrls: ['./helo-delivery-locations.component.css']
})
export class HeloDeliveryLocationsComponent extends DeliveryLocationsJeevesComponent implements OnInit {
  public formOh: FormGroup;
  public enableEditDeliveryAddress = false;

  private userEmail;
  public isExportCustomer: boolean;
  public isPhoneMandatory = false;
  // private orderEmailConfirmation = 'order.se@tylohelo.com'; // Used if checkout.extra.oh.edit filled
  blockEditAddress: boolean = true;

  constructor(
    protected selectedCustomerService: SelectedCustomerService,
    protected deliveryLocationsService: DeliveryLocationsService,
    protected formBuilder: FormBuilder,
    protected authService: AuthService,
    protected tyloCustomerUtilsService: TyloCustomerUtilsService
  ) {
    super(selectedCustomerService, deliveryLocationsService, formBuilder, authService);
  }

  ngOnInit() {
    this.onNextBtnClicked.subscribe(_ => this.handleNextButtonClicked());
    this.selectedCustomerService
      .getSelectedCustomer()
      .pipe(
        first(),
        mergeMap(selectedCustomer =>
          forkJoin([
            of(selectedCustomer),
            this.deliveryLocationsService.getDeliveryLocationsForCustomer(selectedCustomer.id),
            this.authService.getCurrentUser().pipe(first())
          ])
        )
      )
      .subscribe(([selectedCustomer, deliveryLocations, user]) => {
        this.checkout.extra.isExportCustomer = this.tyloCustomerUtilsService.isSelectedCustomerExport(selectedCustomer);
        this.checkout.extra.customerVATCode = this.tyloCustomerUtilsService.getCustomerVATCode(selectedCustomer);

        this.selectedCustomer = selectedCustomer;
        this.blockEditAddress = selectedCustomer.extra.kus.levsattkod === 3 || selectedCustomer.extra.kus.levsattkod === 64
        this.userEmail = user.email;
        this.initForm();
        this.deliveryLocations = [...deliveryLocations];

        if (this.selectedCustomer.extra.kus.ordlevplats1) {
          const deliveryLocation = this.deliveryLocations.find(location => {
            return location.extra.lp.ordlevplats1 === this.selectedCustomer.extra.kus.ordlevplats1;
          });
          this.deliveryLocationId = deliveryLocation.id;
        }
      });
  }

  public get deliveryLocationId(): string {
    return this.selectedDeliveryLocation ? this.selectedDeliveryLocation.id : '';
  }

  public set deliveryLocationId(deliveryLocationId: string) {
    // https://github.com/microsoft/TypeScript/issues/338
    // SHould not implement logic on setter, rather delegate it to method in order to allow overrides
    this.afterDeliveryLocationSet(deliveryLocationId);
  }

  protected handleNextButtonClicked(): void {
    if (this.form.invalid || this.formOh.invalid) {
      this.form.markAllAsTouched();
      this.formOh.markAllAsTouched();
      return;
    }

    this.assignFormValues2(this.form);
    const rawData = this.form.getRawValue();
    this.assignPostalCodeAndCity(rawData.ftgpostnr, rawData.ordlevadr4);
    this.stepDone.emit(this.checkout);
  }

  protected assignPostalCodeAndCity(postalCode: string, city: string): void {
    this.checkout.extra.procargs.ordlevadr4 = (city || '').trim();
    this.checkout.extra.procargs.ftgpostnr = (postalCode || '').trim();
  }

  private assignFormValues2(form: FormGroup) {
    const rawFormData = form.getRawValue();
    const rawformOhData = this.formOh.getRawValue();

    this.checkout.extra.oh = {
      ...this.checkout.extra.oh,
      ...rawformOhData
    };
    /* const emails = [rawformOhData.ta_mailnotified];
        if (!!this.formOh.get('edit').value) {
          emails.push(this.orderEmailConfirmation);
        }
        this.checkout.extra.confirmationEmails = emails; */
    // Check if user changed the delivery addres
    if (
      this.tyloCustomerUtilsService.isFreightConditional(this.selectedCustomer) &&
      ((this.selectedCustomer.extra.fr.ftgpostadr1 || '') !== rawFormData.ordlevadr2 ||
        (this.selectedCustomer.extra.fr.ftgpostadr5 || '') !== rawFormData.ordlevadr3 ||
        (this.selectedCustomer.extra.fr.ftglevpostnr || this.selectedCustomer.extra.fr.ftgpostnr || '') !==
          rawFormData.ftgpostnr ||
        (this.selectedCustomer.extra.fr.ftgpostlevadr3 || '') !== rawFormData.ordlevadr4 ||
        (this.selectedCustomer.extra.fr.landskod || '') !== rawFormData.ordlevadrlandskod)
    ) {
      this.checkout.extra._deliveryAddressEdited = true;
      this.checkout.extra.procargs.levvillkkod = 99;
    } else {
      this.checkout.extra._deliveryAddressEdited = false;
      this.checkout.extra.procargs.levvillkkod = this.selectedCustomer.extra.kus.levvillkkod;
    }

    if (rawformOhData.edit && (rawformOhData.edit as string).trim().length > 0) {
      if (!this.checkout.extra.confirmationEmails) {
        this.checkout.extra.confirmationEmails = [];
      }
    }

    this.checkout.extra.procargs = {
      ...this.checkout.extra.procargs,
      ...rawFormData
    };
    console.log(this.checkout);
  }

  protected initForm() {
    const isHeloRequired = Validators.required;
    const isPhoneRequired = Validators.nullValidator;
    this.form = this.formBuilder.group({
      ordlevadr1: this.formBuilder.control(this.checkout.extra.procargs.ordlevadr1 || '', {
        validators: [Validators.required]
      }),
      ordlevadr2: this.formBuilder.control(this.checkout.extra.procargs.ordlevadr2 || '', [isHeloRequired]),
      ordlevadr3: this.formBuilder.control(this.checkout.extra.procargs.ordlevadr3 || '', [isHeloRequired]),
      ordlevadr4: this.formBuilder.control(this.checkout.extra.procargs.ordlevadr4 || '', [isHeloRequired]),
      ftgpostnr: this.formBuilder.control(this.checkout.extra.procargs.ftgpostnr || '', [isHeloRequired]),
      ordlevadrlandskod: this.formBuilder.control(this.checkout.extra.procargs.ordlevadrlandskod || '', [
        isHeloRequired
      ])
    });
    this.formOh = this.formBuilder.group({
      kundref2: this.formBuilder.control(this.checkout.extra.oh.kundref2 || ''),
      kundbestnr: this.formBuilder.control(this.checkout.extra.oh.kundbestnr || '', [Validators.required]),
      editext: this.formBuilder.control(this.checkout.extra.oh.editext || ''),
      godsmarke1: this.formBuilder.control(this.checkout.extra.oh.godsmarke1 || ''),
      q_oh_teleavisering: this.formBuilder.control(this.checkout.extra.oh.q_oh_teleavisering || '', [isPhoneRequired]),
      ta_mailnotified: this.formBuilder.control(this.checkout.extra.oh.ta_mailnotified || this.userEmail || '', [
        Validators.email
      ])
    });
  }

  toggleEditDeliveryAddress(): void {
    this.enableEditDeliveryAddress = !this.enableEditDeliveryAddress;
    this.initForm();
  }
}
